import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { X as CloseIcon } from "react-feather";

import {
  ADMIN_ZONE_PERFORMANCE,
  ADMIN_ZONE_PERFORMANCE_EVENTS,
} from "../../gql/queries/eventInsights";
import type { ZoneLabel } from "../../types/graphql";
import Explainer from "./Explainer";
import {
  deltaText,
  fmtInt,
  METRIC_BY_KEY,
  METRICS,
  rateColour,
  rgba,
  zoneHex,
  zoneIsDark,
  zoneRgb,
} from "./helpers";
import type { ZonePerf, ZonePerformance, ZonePerformanceEvents, ZonePeriod } from "./types";
import WorkedExample from "./WorkedExample";
import ZoneClockReference from "./ZoneClockReference";

const ZoneDot: React.FC<{ zone: ZoneLabel; size?: number }> = ({ zone, size = 10 }) => (
  <Box
    component="span"
    sx={{
      width: size,
      height: size,
      borderRadius: "50%",
      backgroundColor: zoneHex(zone),
      display: "inline-block",
      flex: "0 0 auto",
    }}
  />
);

/** A ▲▼ delta as a small coloured span. */
const Delta: React.FC<{ cur: number; prev: number; unit?: string }> = ({ cur, prev, unit }) => {
  const d = deltaText(cur, prev, unit);
  if (!d) {
    return null;
  }
  return (
    <Box component="span" sx={{ ml: 0.5, fontSize: 11, color: d.up ? "#00583D" : "#A32D2D" }}>
      {d.text}
    </Box>
  );
};

interface DrillTarget {
  zone: ZoneLabel;
  eventType: string;
  label: string;
}

const ZonePerformancePanel: React.FC = () => {
  const [quarter, setQuarter] = useState("");
  const [mode, setMode] = useState<"month" | "qoq">("month");
  const [view, setView] = useState<"matrix" | "cards">("matrix");
  const [metricKey, setMetricKey] = useState("avg_show");
  const [drill, setDrill] = useState<DrillTarget | null>(null);

  const { data, loading, error } = useQuery(ADMIN_ZONE_PERFORMANCE, {
    variables: { quarter: quarter || null },
    fetchPolicy: "cache-and-network",
  });
  const perf: ZonePerformance | undefined = data?.adminZonePerformance;

  // "This month" only exists for the current quarter — fall back to QoQ otherwise.
  useEffect(() => {
    if (perf && !perf.isCurrent && mode === "month") {
      setMode("qoq");
    }
  }, [perf, mode]);

  const metric = METRIC_BY_KEY.get(metricKey) ?? METRICS[0];

  const cur: ZonePeriod | null = useMemo(() => {
    if (!perf) {
      return null;
    }
    return mode === "month" ? (perf.month ?? perf.quarter) : perf.quarter;
  }, [perf, mode]);

  const prev: ZonePeriod | null = useMemo(() => {
    if (!perf || mode === "month" || view === "matrix") {
      return null;
    }
    return perf.prevQuarter;
  }, [perf, mode, view]);

  if (loading && !perf) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">Couldn’t load zone performance. {error.message}</Alert>;
  }
  if (!perf || !cur) {
    return null;
  }

  const zoneLut = new Map<ZoneLabel, ZonePerf>(cur.zones.map((z) => [z.zone, z]));
  const presentZones = perf.zoneOrder.filter((z) => zoneLut.has(z));

  const sum = (f: (z: ZonePerf) => number): number => cur.zones.reduce((n, z) => n + f(z), 0);
  const kEvents = sum((z) => z.totals.events);
  const kRsvps = sum((z) => z.totals.rsvps);
  const kShowed = sum((z) => z.totals.showed);
  const kRate = kRsvps ? Math.round((1000 * kShowed) / kRsvps) / 10 : 0;
  const prevKEvents = prev ? prev.zones.reduce((n, z) => n + z.totals.events, 0) : 0;
  const prevKRsvps = prev ? prev.zones.reduce((n, z) => n + z.totals.rsvps, 0) : 0;
  const prevKShowed = prev ? prev.zones.reduce((n, z) => n + z.totals.showed, 0) : 0;
  const prevKRate = prevKRsvps ? Math.round((1000 * prevKShowed) / prevKRsvps) / 10 : 0;

  return (
    <Box>
      {/* Controls */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap mb={2}>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
          <ToggleButton value="matrix">Matrix</ToggleButton>
          <ToggleButton value="cards">Cards</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)}>
          {perf.isCurrent && <ToggleButton value="month">This month</ToggleButton>}
          <ToggleButton value="qoq">Quarter‑over‑quarter</ToggleButton>
        </ToggleButtonGroup>
        {view === "matrix" && (
          <TextField
            select
            size="small"
            label="Shade by"
            value={metricKey}
            onChange={(e) => setMetricKey(e.target.value)}
            sx={{ minWidth: 210 }}
          >
            {METRICS.map((m) => (
              <MenuItem key={m.key} value={m.key}>
                {m.label}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          size="small"
          label="Quarter"
          value={perf.selectedQuarter}
          onChange={(e) => setQuarter(e.target.value)}
          sx={{ minWidth: 190 }}
        >
          {perf.quarters.map((q) => (
            <MenuItem key={q.value} value={q.value}>
              {q.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {/* KPI summary */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap mb={2}>
        <Kpi
          label="Events"
          value={fmtInt(kEvents)}
          delta={prev ? <Delta cur={kEvents} prev={prevKEvents} /> : null}
        />
        <Kpi label="RSVPs" value={fmtInt(kRsvps)} />
        <Kpi label="Showed up" value={fmtInt(kShowed)} />
        <Kpi
          label="Show‑up rate"
          value={`${kRate}%`}
          delta={prev ? <Delta cur={kRate} prev={prevKRate} unit="pt" /> : null}
        />
      </Stack>

      <Typography variant="body2" color="textSecondary" mb={1.5}>
        {view === "matrix"
          ? `${cur.label} — each row shaded by ${metric.label}; the ringed cell is the top zone for that event type.`
          : mode === "month"
            ? `Showing ${cur.label}.`
            : `Showing ${cur.label} vs ${perf.prevQuarter.label} (▲▼ = change vs last quarter).`}
      </Typography>

      <ZoneClockReference />

      {presentZones.length === 0 ? (
        <Alert severity="info">No events in this period.</Alert>
      ) : view === "matrix" ? (
        <Matrix zones={presentZones} lut={zoneLut} metricKey={metricKey} onDrill={setDrill} />
      ) : (
        <Cards zones={presentZones} lut={zoneLut} prev={prev} />
      )}

      {presentZones.length > 0 && <DemandIndex zones={presentZones} lut={zoneLut} />}

      {drill && (
        <DrillDialog
          target={drill}
          period={mode === "month" ? "month" : "quarter"}
          quarter={quarter}
          onClose={() => setDrill(null)}
        />
      )}
    </Box>
  );
};

const Kpi: React.FC<{ label: string; value: string; delta?: React.ReactNode }> = ({
  label,
  value,
  delta,
}) => (
  <Paper variant="outlined" sx={{ px: 2, py: 1.25, minWidth: 130, borderRadius: 2 }}>
    <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
      {value}
    </Typography>
    <Typography variant="caption" color="textSecondary">
      {label}
      {delta}
    </Typography>
  </Paper>
);

// ── Matrix view ─────────────────────────────────────────────────────────────
const Matrix: React.FC<{
  zones: ZoneLabel[];
  lut: Map<ZoneLabel, ZonePerf>;
  metricKey: string;
  onDrill: (t: DrillTarget) => void;
}> = ({ zones, lut, metricKey, onDrill }) => {
  const metric = METRIC_BY_KEY.get(metricKey) ?? METRICS[0];

  // Event types across all present zones, busiest first.
  const totals = new Map<string, number>();
  const label = new Map<string, string>();
  const cell = new Map<string, ZonePerf["types"][number]>();
  zones.forEach((z) => {
    (lut.get(z)?.types ?? []).forEach((t) => {
      totals.set(t.type, (totals.get(t.type) ?? 0) + t.events);
      label.set(t.type, t.typeLabel);
      cell.set(`${t.type}|${z}`, t);
    });
  });
  const types = [...totals.keys()].sort((a, b) => (totals.get(b) ?? 0) - (totals.get(a) ?? 0));

  const gridTemplate = `210px repeat(${zones.length}, minmax(56px, 1fr)) 140px`;
  const headSx = {
    fontSize: 11,
    fontWeight: 600,
    color: "text.secondary",
    px: 1,
    py: 0.75,
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    borderBottom: "1px solid",
    borderColor: "divider",
  } as const;

  const cells: React.ReactNode[] = [];
  // Header
  cells.push(
    <Box key="corner" sx={{ ...headSx, fontWeight: 700 }}>
      Event type ╲ Zone
    </Box>,
  );
  zones.forEach((z) => {
    cells.push(
      <Box key={`h-${z}`} sx={{ ...headSx, justifyContent: "center" }}>
        <ZoneDot zone={z} /> {z}
      </Box>,
    );
  });
  cells.push(
    <Box key="h-result" sx={{ ...headSx, justifyContent: "flex-end" }}>
      {metric.bad ? "Most ⚠" : "Top zone"}
    </Box>,
  );

  // Type rows
  types.forEach((type) => {
    const vals = zones.map((z) => {
      const t = cell.get(`${type}|${z}`);
      return t ? metric.fn(t) : null;
    });
    const present = vals.filter((v): v is number => v != null);
    const max = present.length ? Math.max(...present) : 0;
    const winIdx = vals.indexOf(max);
    cells.push(
      <Box
        key={`l-${type}`}
        sx={{
          fontSize: 12,
          px: 1,
          py: 0.75,
          borderBottom: "1px solid",
          borderColor: "action.hover",
        }}
      >
        {label.get(type)}
      </Box>,
    );
    zones.forEach((z, i) => {
      const v = vals[i];
      const t = cell.get(`${type}|${z}`);
      if (v == null || !t) {
        cells.push(
          <Box
            key={`c-${type}-${z}`}
            sx={{
              fontSize: 12,
              textAlign: "center",
              color: "text.disabled",
              py: 0.75,
              borderBottom: "1px solid",
              borderColor: "action.hover",
            }}
          >
            ·
          </Box>,
        );
        return;
      }
      const alpha = max ? 0.12 + 0.88 * (v / max) : 0;
      const dark = zoneIsDark(z) && alpha > 0.55;
      const best = v === max && max > 0;
      const tip = `${z} · ${label.get(type)}
${metric.fmt(v)} ${metric.label}
${fmtInt(t.events)} events · ${fmtInt(t.showed)} showed · ${t.showUpRate}% rate${
        t.noShowEvents ? `\n⚠ ${t.noShowEvents} event(s) with no attendance` : ""
      }
(click to see the ${fmtInt(t.events)} event(s))`;
      cells.push(
        <Tooltip
          key={`c-${type}-${z}`}
          title={<Box sx={{ whiteSpace: "pre-line" }}>{tip}</Box>}
          arrow
        >
          <Box
            role="button"
            onClick={() => onDrill({ zone: z, eventType: type, label: label.get(type) ?? type })}
            sx={{
              cursor: "pointer",
              fontSize: 12,
              fontWeight: best ? 700 : 500,
              textAlign: "center",
              py: 0.75,
              color: dark ? "#fff" : "text.primary",
              backgroundColor: rgba(zoneRgb(z), alpha),
              outline: best ? `2px solid ${zoneHex(z)}` : "none",
              outlineOffset: "-2px",
              borderBottom: "1px solid",
              borderColor: "action.hover",
              "&:hover": { filter: "brightness(0.95)" },
            }}
          >
            {metric.fmt(v)}
          </Box>
        </Tooltip>,
      );
    });
    // Result column — the leading zone for this event type.
    if (max > 0 && winIdx >= 0) {
      const wz = zones[winIdx];
      cells.push(
        <Box
          key={`r-${type}`}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.5,
            fontSize: 12,
            px: 1,
            py: 0.75,
            borderBottom: "1px solid",
            borderColor: "action.hover",
          }}
        >
          <ZoneDot zone={wz} /> <strong>{wz}</strong>
          <Box component="span" sx={{ color: "text.secondary" }}>
            {metric.fmt(max)}
          </Box>
        </Box>,
      );
    } else {
      cells.push(
        <Box key={`r-${type}`} sx={{ textAlign: "right", color: "text.disabled", px: 1, py: 0.75 }}>
          —
        </Box>,
      );
    }
  });

  // Totals row
  const ztot = zones.map((z) => metric.total(lut.get(z) as ZonePerf));
  const tmax = ztot.length ? Math.max(...ztot) : 0;
  const tWinIdx = ztot.indexOf(tmax);
  cells.push(
    <Box key="l-total" sx={{ fontSize: 12, fontWeight: 700, px: 1, py: 0.75 }}>
      ▦ All event types
    </Box>,
  );
  zones.forEach((z, i) => {
    const v = ztot[i];
    const alpha = tmax ? 0.12 + 0.88 * (v / tmax) : 0;
    const dark = zoneIsDark(z) && alpha > 0.55;
    const best = v === tmax && tmax > 0;
    cells.push(
      <Tooltip
        key={`t-${z}`}
        title={
          <Box
            sx={{ whiteSpace: "pre-line" }}
          >{`${z} · all event types\n(click to see every event in ${z})`}</Box>
        }
        arrow
      >
        <Box
          role="button"
          onClick={() => onDrill({ zone: z, eventType: "", label: "all event types" })}
          sx={{
            cursor: "pointer",
            fontSize: 12,
            fontWeight: best ? 700 : 600,
            textAlign: "center",
            py: 0.75,
            color: dark ? "#fff" : "text.primary",
            backgroundColor: rgba(zoneRgb(z), alpha),
            outline: best ? `2px solid ${zoneHex(z)}` : "none",
            outlineOffset: "-2px",
          }}
        >
          {metric.fmt(v)}
        </Box>
      </Tooltip>,
    );
  });
  if (tmax > 0) {
    const wz = zones[tWinIdx];
    cells.push(
      <Box
        key="t-result"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 0.5,
          fontSize: 12,
          px: 1,
        }}
      >
        <ZoneDot zone={wz} /> <strong>{wz}</strong>
      </Box>,
    );
  } else {
    cells.push(
      <Box key="t-result" sx={{ textAlign: "right", color: "text.disabled", px: 1 }}>
        —
      </Box>,
    );
  }

  return (
    <Box>
      <Box sx={{ overflowX: "auto" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: gridTemplate,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            minWidth: 640,
            alignItems: "stretch",
          }}
        >
          {cells}
        </Box>
      </Box>
      <Typography variant="caption" color="textSecondary" component="p" mt={1}>
        Each cell is shaded in <b>its zone’s colour</b>, deeper = higher <b>{metric.label}</b> (per
        row). The ringed cell is where each event type{" "}
        {metric.bad ? "has the most no‑shows" : "does best"}. The bottom <b>All event types</b> row
        is each zone’s overall figure. <b>·</b> = that type wasn’t run in that zone. Click any cell
        to see the events behind it.
      </Typography>
    </Box>
  );
};

// ── Cards view ──────────────────────────────────────────────────────────────
const Cards: React.FC<{
  zones: ZoneLabel[];
  lut: Map<ZoneLabel, ZonePerf>;
  prev: ZonePeriod | null;
}> = ({ zones, lut, prev }) => {
  const prevLut = new Map<ZoneLabel, ZonePerf>((prev?.zones ?? []).map((z) => [z.zone, z]));
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
        gap: 2,
      }}
    >
      {zones.map((z) => {
        const zd = lut.get(z) as ZonePerf;
        const pz = prevLut.get(z);
        return (
          <Paper
            key={z}
            variant="outlined"
            sx={{ p: 2, borderRadius: 2, borderTop: `3px solid ${zoneHex(z)}` }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <ZoneDot zone={z} />
                <Typography sx={{ fontWeight: 700 }}>{z}</Typography>
              </Stack>
              <Typography variant="body2" color="textSecondary">
                <strong>{fmtInt(zd.totals.events)}</strong> events
                {pz ? <Delta cur={zd.totals.events} prev={pz.totals.events} /> : null} ·{" "}
                <strong>{zd.totals.showUpRate}%</strong> show‑up
              </Typography>
            </Stack>
            {zd.types.map((t) => {
              const prevRow = pz?.types.find((x) => x.type === t.type);
              return (
                <Stack
                  key={t.type}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ py: 0.4 }}
                >
                  <Tooltip title={t.typeLabel} arrow>
                    <Box
                      sx={{
                        width: 150,
                        fontSize: 12,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t.typeLabel}
                    </Box>
                  </Tooltip>
                  <Box
                    sx={{
                      flex: 1,
                      height: 10,
                      borderRadius: 1,
                      backgroundColor: "action.hover",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${Math.max(2, t.share)}%`,
                        height: "100%",
                        backgroundColor: zoneHex(z),
                      }}
                    />
                  </Box>
                  <Box sx={{ width: 64, textAlign: "right", fontSize: 12 }}>
                    {fmtInt(t.showed)}
                    <Box component="span" sx={{ color: "text.secondary", fontSize: 10 }}>
                      {" "}
                      showed
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      width: 56,
                      textAlign: "right",
                      fontSize: 12,
                      fontWeight: 700,
                      color: rateColour(t.showUpRate),
                    }}
                  >
                    {t.showUpRate}%
                    {prevRow ? <Delta cur={t.showUpRate} prev={prevRow.showUpRate} /> : null}
                  </Box>
                </Stack>
              );
            })}
          </Paper>
        );
      })}
    </Box>
  );
};

// ── Demand index (turnout vs supply) ────────────────────────────────────────
// Answers "is this zone busy because members love that time, or just because we
// run lots of events then?" — each zone's attendance-per-event vs the average.
const DemandIndex: React.FC<{ zones: ZoneLabel[]; lut: Map<ZoneLabel, ZonePerf> }> = ({
  zones,
  lut,
}) => {
  const totalEvents = zones.reduce((n, z) => n + (lut.get(z)?.totals.events ?? 0), 0);
  const totalShowed = zones.reduce((n, z) => n + (lut.get(z)?.totals.showed ?? 0), 0);
  const overall = totalEvents ? totalShowed / totalEvents : 0;

  const rows = zones
    .map((z) => {
      const t = lut.get(z) as ZonePerf;
      const perEvent = t.totals.events ? t.totals.showed / t.totals.events : 0;
      return {
        zone: z,
        index: overall ? perEvent / overall : 0,
        perEvent,
        events: t.totals.events,
      };
    })
    .sort((a, b) => b.index - a.index);

  const verdict = (i: number): { text: string; colour: string } =>
    i >= 1.15
      ? { text: "real pull", colour: "#00583D" }
      : i <= 0.85
        ? { text: "just supply", colour: "#A32D2D" }
        : { text: "in line with supply", colour: "text.secondary" };

  return (
    <Box mt={3}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        Demand by zone — turnout vs. how many events we run
      </Typography>
      <Explainer title="How the demand index is worked out">
        A zone can look busy just because we schedule a lot there. So instead of counting
        attendances, we compare each zone’s <b>turnout per event</b> to the average across all
        zones. <b>Above 1.0×</b> = members turn out harder than the number of events alone explains
        — <b>real demand</b>. <b>Below 1.0×</b> = we’re offering more than the turnout justifies. A
        quiet zone scoring high is a time members want that we’re under‑serving.
      </Explainer>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {rows.map((r) => {
          const v = verdict(r.index);
          const fill = Math.min(r.index / 2, 1) * 100; // 2.0× fills the bar; tick sits at 1.0×
          return (
            <Stack key={r.zone} direction="row" spacing={1.5} alignItems="center">
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ width: 96, flex: "0 0 auto" }}
              >
                <ZoneDot zone={r.zone} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {r.zone}
                </Typography>
              </Stack>
              <Box
                sx={{
                  position: "relative",
                  flex: 1,
                  height: 14,
                  borderRadius: 1,
                  backgroundColor: "action.hover",
                }}
              >
                {/* 1.0× reference tick */}
                <Box
                  sx={{
                    position: "absolute",
                    left: "50%",
                    top: -2,
                    bottom: -2,
                    width: "1px",
                    backgroundColor: "divider",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${fill}%`,
                    borderRadius: 1,
                    backgroundColor: zoneHex(r.zone),
                    opacity: 0.85,
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  width: 150,
                  flex: "0 0 auto",
                  textAlign: "right",
                  color: v.colour,
                  fontWeight: 600,
                }}
              >
                {r.index.toFixed(2)}×{" "}
                <Box component="span" sx={{ fontWeight: 400 }}>
                  · {v.text}
                </Box>
              </Typography>
            </Stack>
          );
        })}
      </Box>
      <Typography variant="caption" color="textSecondary" component="p" mt={1}>
        The line down the middle is 1.0× (turnout exactly matches supply). Bars reaching past it are
        zones members show up for more than our schedule alone would explain.
      </Typography>
      <WorkedExample
        title="Worked example — one month"
        intro='Two example zones. "Total who showed up" is every member across all those events — not one person:'
        columns={["Zone", "Events", "Total who showed up", "Per event"]}
        rows={[
          ["GREEN", "100", "800", "8.0"],
          ["RED", "5", "60", "12.0"],
        ]}
        conclusion={
          <>
            GREEN’s <b>100 events</b> drew <b>800 attendances</b> between them — that’s{" "}
            <b>8.0 people per event</b>. Across all eight zones the average is also about 8.0, so
            GREEN = 8.0 ÷ 8.0 = <b>1.0×</b> (busy only because we run lots there). RED ran just{" "}
            <b>5 events</b> but drew <b>60</b> — <b>12.0 per event</b> = 12.0 ÷ 8.0 = <b>1.5×</b>,
            so each RED event pulls 50% more than average — real demand we’re barely serving.
          </>
        }
      />
    </Box>
  );
};

// ── Drill-down dialog ───────────────────────────────────────────────────────
const DrillDialog: React.FC<{
  target: DrillTarget;
  period: string;
  quarter: string;
  onClose: () => void;
}> = ({ target, period, quarter, onClose }) => {
  const { data, loading, error } = useQuery(ADMIN_ZONE_PERFORMANCE_EVENTS, {
    variables: {
      period,
      zone: target.zone,
      eventType: target.eventType || null,
      quarter: quarter || null,
    },
    fetchPolicy: "network-only",
  });
  const result: ZonePerformanceEvents | undefined = data?.adminZonePerformanceEvents;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <ZoneDot zone={target.zone} />
          <span>
            {target.zone} · {target.label}
          </span>
        </Stack>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && !result ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error.message}</Alert>
        ) : !result || result.events.length === 0 ? (
          <Typography color="textSecondary">No events behind this cell.</Typography>
        ) : (
          <>
            <Stack direction="row" spacing={1} mb={1.5} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={`${result.periodLabel}`} />
              <Chip size="small" label={`${result.count} events`} />
              <Chip size="small" label={`${fmtInt(result.totalRsvps)} RSVPs`} />
              <Chip size="small" label={`${fmtInt(result.totalAttended)} showed`} />
              {result.noAttendance > 0 && (
                <Chip size="small" color="warning" label={`${result.noAttendance} with nobody`} />
              )}
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When (UTC)</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Host</TableCell>
                  {!target.eventType && <TableCell>Type</TableCell>}
                  <TableCell align="right">RSVPs</TableCell>
                  <TableCell align="right">Showed</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {result.events.map((e) => (
                  <TableRow key={e.eventId} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{e.whenUtc}</TableCell>
                    <TableCell>{e.title}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{e.host ?? "—"}</TableCell>
                    {!target.eventType && (
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{e.typeLabel}</TableCell>
                    )}
                    <TableCell align="right">{e.rsvps}</TableCell>
                    <TableCell align="right">{e.attended}</TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: rateColour(e.showRate), fontWeight: 600 }}
                    >
                      {e.showRate}%
                    </TableCell>
                    <TableCell align="right">
                      <Link
                        href={`https://admin.heylady.io/event/${e.eventId}/participants`}
                        target="_blank"
                        rel="noopener"
                        variant="body2"
                      >
                        open ↗
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ZonePerformancePanel;
